/**
 * HelpTooltip Component
 *
 * A contextual help icon that shows a popover with helpful information.
 * Click the icon to display help text in a popover (not a tooltip).
 *
 * USAGE:
 * ```tsx
 * <Box sx={{ display: 'flex', alignItems: 'center' }}>
 *   <Typography>Field Label</Typography>
 *   <HelpTooltip title="This field is used for..." />
 * </Box>
 * ```
 */

import React, { useState } from 'react';
import { IconButton, Popover, Typography, Box } from '@mui/material';
import { HelpOutlineRounded as HelpIcon } from '@mui/icons-material';

interface HelpTooltipProps {
  /** Help text to display in the popover */
  title: string;
  /** Position of the popover relative to the icon */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Contextual help component - clickable icon that shows help in a popover
 * Consistent across the entire application
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  title,
  placement = 'right',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          ml: 0.5,
          color: 'text.secondary',
          opacity: 0.5,
          transition: 'all 0.2s',
          '&:hover': {
            opacity: 1,
            color: 'primary.main',
          },
        }}
      >
        <HelpIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: placement === 'top' ? 'top' : placement === 'bottom' ? 'bottom' : 'center',
          horizontal: placement === 'left' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: placement === 'top' ? 'bottom' : placement === 'bottom' ? 'top' : 'center',
          horizontal: placement === 'left' ? 'right' : 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            maxWidth: 400,
            boxShadow: (theme) => theme.shadows[8],
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.6, textAlign: 'justify' }}>
            {title}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default HelpTooltip;
